import type { CSSProperties, FC, MouseEvent, ReactNode, RefObject } from 'react';
import { Z } from '@components/v4/zIndex';

type NavBarBackgroundProps = {
  isExpanded?: boolean;
  /**
   * Compact desktop (1024–1279): the expanded sidebar is a temporary drawer. The 72px rail keeps
   * its slot in flow — so the content area never loses a pixel — and the labelled panel floats
   * over the content, above the fixed header but below the order toast (see `zIndex.ts`).
   */
  isDrawerOpen?: boolean;
  panelId?: string;
  panelRef?: RefObject<HTMLDivElement>;
  onBackdropClick?(): void;
  onPanelClick?(event: MouseEvent<HTMLDivElement>): void;
  children: ReactNode;
};

const EXPANDED_WIDTH = 240;

// The panel is ALWAYS the same node (only its positioning changes), so opening/closing the drawer
// never unmounts the sidebar tree nor loses the scroll position of the module list.
const panelStyle = (isDrawerOpen: boolean): CSSProperties =>
  isDrawerOpen
    ? {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: EXPANDED_WIDTH,
        display: 'flex',
        background: '#FFFFFF',
        borderRight: '1px solid #E5ECEE',
        boxShadow: '0 12px 32px rgba(37,55,70,.18)',
        zIndex: Z.navDrawer,
        outline: 'none',
      }
    : { display: 'flex', width: '100%', height: '100%', outline: 'none' };

const NavBarBackground: FC<NavBarBackgroundProps> = ({
  isExpanded,
  isDrawerOpen = false,
  panelId,
  panelRef,
  onBackdropClick,
  onPanelClick,
  children,
}) => {
  // While the drawer is open the rail keeps its 72px slot in flow, so the content never reflows.
  const isPushExpanded = Boolean(isExpanded) && !isDrawerOpen;

  return (
    <div
      className={`w-full flex lg:relative items-center justify-center bg-color-gray_light lg:bg-color-white z-10 lg:transition-[width] lg:duration-[250ms] ${
        isPushExpanded ? 'lg:w-[240px]' : 'lg:w-[72px]'
      }`}
      style={{ borderRight: '1px solid #E5ECEE', zIndex: isDrawerOpen ? Z.navDrawer : undefined }}
    >
      {isDrawerOpen && (
        // Backdrop: tap outside to close. A <button> so it is reachable by keyboard too.
        <button
          type="button"
          aria-label="Cerrar menú"
          onClick={onBackdropClick}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(37,55,70,.45)',
            border: 0,
            padding: 0,
            cursor: 'pointer',
            zIndex: Z.navDrawerBackdrop,
          }}
        />
      )}
      <div
        ref={panelRef}
        id={panelId}
        role={isDrawerOpen ? 'dialog' : undefined}
        aria-modal={isDrawerOpen ? true : undefined}
        aria-label={isDrawerOpen ? 'Menú de navegación' : undefined}
        tabIndex={isDrawerOpen ? -1 : undefined}
        onClick={onPanelClick}
        style={panelStyle(isDrawerOpen)}
      >
        {children}
      </div>
    </div>
  );
};

export default NavBarBackground;
